import React from "react";
import { NavLink } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";
import { HOME_PAGE_URL, PRIVACY_POLICY_PAGE_URL } from "../../utilities/constants/PagesEndpoints";

const TermsOfServicePage = () => {
  const lastUpdated = "February 15, 2026";

  return (
    <div className="bg-slate-50 dark:bg-slate-950 transition-colors duration-300 min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-r from-slate-700 to-slate-800 dark:from-slate-800 dark:to-slate-900 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-white" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              Terms of Service
            </h1>
          </div>
          <p className="text-slate-300 text-lg">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <NavLink
            to={HOME_PAGE_URL}
            className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </NavLink>

          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                1. Introduction
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Welcome to Le Prestige Hall. These Terms of Service ("Terms") govern your use of our
                website, services, and facilities located in Columbus, Ohio. By accessing or using our
                services, you agree to be bound by these Terms. If you do not agree to these Terms,
                please do not use our services.
              </p>
            </section>

            {/* Services */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                2. Services
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                Le Prestige Hall provides event venue rental services, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2 ml-4">
                <li>Banquet hall rental for weddings, receptions, and celebrations</li>
                <li>Corporate event hosting and meeting spaces</li>
                <li>Catering coordination and add-on services</li>
                <li>Event planning consultation and appointment scheduling</li>
                <li>Audio/visual equipment and decoration services</li>
              </ul>
            </section>

            {/* Reservations and Payments */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                3. Reservations and Payments
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-slate-300 leading-relaxed">
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">3.1 Booking:</strong> All
                  reservations must be made through our website or by contacting our staff directly.
                  A reservation is not confirmed until you receive written confirmation from Le Prestige Hall.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">3.2 Deposits:</strong> A
                  non-refundable deposit is required to secure your reservation. The deposit amount
                  will be specified at the time of booking.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">3.3 Payment:</strong> Full
                  payment is due according to the payment schedule outlined in your reservation agreement.
                  We accept major credit cards, bank transfers, and other approved payment methods.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">3.4 Pricing:</strong> All
                  prices are subject to change without notice. However, confirmed reservations will
                  honor the pricing agreed upon at the time of booking.
                </p>
              </div>
            </section>

            {/* Cancellation Policy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                4. Cancellation Policy
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-slate-300 leading-relaxed">
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">4.1</strong> Cancellations
                  made more than 90 days before the event date may be eligible for a partial refund,
                  excluding the deposit.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">4.2</strong> Cancellations
                  made within 90 days of the event date are non-refundable.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">4.3</strong> Le Prestige Hall
                  reserves the right to cancel or reschedule events due to unforeseen circumstances,
                  including but not limited to natural disasters, government mandates, or facility issues.
                  In such cases, a full refund or rescheduling option will be provided.
                </p>
              </div>
            </section>

            {/* User Responsibilities */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                5. User Responsibilities
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-slate-300 leading-relaxed">
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">5.1</strong> You agree to
                  provide accurate and complete information when making reservations or creating an account.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">5.2</strong> You are responsible
                  for the conduct of all guests attending your event and any damages caused to our facility.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">5.3</strong> You agree to
                  comply with all applicable laws, regulations, and our facility rules during your event.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">5.4</strong> Smoking is
                  prohibited inside the facility. Designated smoking areas are available outside.
                </p>
              </div>
            </section>

            {/* Liability */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                6. Limitation of Liability
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-slate-300 leading-relaxed">
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">6.1</strong> Le Prestige Hall
                  is not responsible for any personal property lost, stolen, or damaged on our premises.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">6.2</strong> To the maximum
                  extent permitted by law, Le Prestige Hall shall not be liable for any indirect,
                  incidental, special, or consequential damages arising from your use of our services.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">6.3</strong> Our total
                  liability shall not exceed the amount paid by you for the specific service giving
                  rise to the claim.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                7. Intellectual Property
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                All content on our website, including text, graphics, logos, images, and software,
                is the property of Le Prestige Hall or its licensors and is protected by copyright
                and other intellectual property laws. You may not reproduce, distribute, or create
                derivative works without our express written permission.
              </p>
            </section>

            {/* Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                8. Privacy
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Your use of our services is also governed by our{" "}
                <NavLink
                  to={PRIVACY_POLICY_PAGE_URL}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Privacy Policy
                </NavLink>
                , which describes how we collect, use, and protect your personal information.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                9. Modifications to Terms
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Le Prestige Hall reserves the right to modify these Terms at any time. Changes will
                be effective immediately upon posting on our website. Your continued use of our
                services after any changes constitutes your acceptance of the new Terms.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                10. Governing Law
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the
                State of Ohio, without regard to its conflict of law provisions. Any disputes arising
                from these Terms shall be resolved in the courts of Franklin County, Ohio.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                11. Contact Us
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-6">
                <p className="text-slate-700 dark:text-slate-200 font-semibold">Le Prestige Hall</p>
                <p className="text-slate-600 dark:text-slate-300">Columbus, Ohio</p>
                <p className="text-slate-600 dark:text-slate-300">
                  Email:{" "}
                  <a
                    href="mailto:contact@leprestigehall.net"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    contact@leprestigehall.net
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
